import { useMemo } from "react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";
import { BarChart3, CalendarCheck, Clock, TrendingUp } from "lucide-react";
import { parseBrazilDate } from "../../utils/date";

interface Props {
  appointments: any[];
  loading: boolean;
}

export function ClientMetrics({ appointments, loading }: Props) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].metricsSection;

  const metrics = useMemo(() => {
    const now = new Date();
    
    // Rolling periods
    const oneMonthAgo = new Date(now); oneMonthAgo.setMonth(now.getMonth() - 1);
    const threeMonthsAgo = new Date(now); threeMonthsAgo.setMonth(now.getMonth() - 3);
    const sixMonthsAgo = new Date(now); sixMonthsAgo.setMonth(now.getMonth() - 6);
    const oneYearAgo = new Date(now); oneYearAgo.setFullYear(now.getFullYear() - 1);

    const counts = {
      month: 0,
      quarter: 0,
      semester: 0,
      year: 0
    };

    appointments.forEach(a => {
      if (!Number(a.done)) return;
      
      const d = parseBrazilDate(a.start_datetime);
      if (d >= oneMonthAgo) counts.month++;
      if (d >= threeMonthsAgo) counts.quarter++;
      if (d >= sixMonthsAgo) counts.semester++;
      if (d >= oneYearAgo) counts.year++;
    });

    return counts;
  }, [appointments]);

  if (loading && appointments.length === 0) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-24 bg-white rounded-3xl border border-neutral-100 animate-pulse" />
        ))}
      </div>
    );
  }

  const items = [
    { label: t.lastMonth, value: metrics.month, icon: Clock, color: "text-blue-500", bg: "bg-blue-50" },
    { label: t.lastQuarter, value: metrics.quarter, icon: CalendarCheck, color: "text-purple-500", bg: "bg-purple-50" },
    { label: t.lastSemester, value: metrics.semester, icon: TrendingUp, color: "text-green-500", bg: "bg-green-50" },
    { label: t.lastYear, value: metrics.year, icon: BarChart3, color: "text-orange-500", bg: "bg-orange-50" },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {items.map((item, idx) => (
        <div key={idx} className="bg-white rounded-3xl p-5 border border-neutral-100 flex flex-col gap-3 transition-all hover:border-neutral-200 group relative overflow-hidden">
          <div className={`w-10 h-10 rounded-2xl ${item.bg} flex items-center justify-center ${item.color} transition-transform group-hover:scale-110`}>
            <item.icon className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-baseline gap-1.5 mb-0.5">
              <span className="text-2xl font-bold text-neutral-900 leading-none">{item.value}</span>
              <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-tighter opacity-70">{t.appointments}</span>
            </div>
            <span className="text-[10px] uppercase font-bold text-neutral-400 tracking-wider">
              {item.label}
            </span>
          </div>
          {/* Subtle background decoration */}
          <div className={`absolute -right-4 -bottom-4 w-16 h-16 rounded-full ${item.bg} opacity-10 transition-transform group-hover:scale-150`} />
        </div>
      ))}
    </div>
  );
}
